/**
 * Email-based feature gating for internal-only / beta features.
 *
 * Currently gated:
 *   (none active — Algolia + Direct Search are both GA)
 *
 * Update 2026-05-19: Algolia-backed Cosmos search is now GA for ALL signed-in
 * users. Previously only the four INTERNAL_TEST_EMAILS were routed to the
 * /algolia variant, while everyone else hit the legacy LanceDB streaming
 * endpoint. That code path was choking the server with a cold-start of a
 * multi-GB embedding model whenever a non-allowlisted user opened Cosmos
 * chat or the For You page. See BUG_2026-05-19_orphan_email_conversations.md
 * for context and the broader LanceDB-deprecation plan.
 *
 * The allowlist itself is preserved for documentation / future targeted
 * rollouts, and `isInternalTestEmail` stays exported in case ops needs it.
 *
 * NOTE: this is a client-side gate suitable for an internal test cohort;
 * it's NOT a security boundary. Anyone determined enough can hit any
 * endpoint directly via DevTools. If you need real authorization, mirror
 * the gate on the backend and reject non-allowlisted callers.
 */

export const INTERNAL_TEST_EMAILS: ReadonlySet<string> = new Set([
  'pranavgarg64@gmail.com',
  'parag@universitytech.io',
  'makarand@universitytech.io',
  'yukta@universitytech.io',
]);

const norm = (email?: string | null): string =>
  (email || '').trim().toLowerCase();

export const isInternalTestEmail = (email?: string | null): boolean => {
  const e = norm(email);
  if (!e) return false;
  return INTERNAL_TEST_EMAILS.has(e);
};

/** Convenience flag readers — keep callers expressive. */
// Direct Search is GA for every signed-in user.
export const canSeeDirectSearch = (_email?: string | null): boolean => true;

// Cosmos chat now ALWAYS routes through the Algolia streaming endpoint —
// the legacy LanceDB path is deprecated and being phased out. Function
// signature preserved so call sites stay unchanged; flip back to
// `isInternalTestEmail(email)` for an emergency rollback to the old cohort.
export const shouldUseAlgoliaForCosmos = (_email?: string | null): boolean => true;
